import { useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import moment from "moment/moment";
// @ts-expect-error pure js
import TextInput from "../../../UI/TextInput";
// @ts-expect-error pure js
import Checkbox from "../../../UI/Checkbox";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../../../UI/SelectStandalone";
import Callout from "../../../UI/Callout";
import Text from "../../../UI/Text";
// @ts-expect-error pure js
import useBalances from "../../../../hooks/use-balances";
import { BalanceDomain } from "../../../../utils/constants/balances";
// @ts-expect-error pure js
import { TVA_RATES } from "../../../../utils/constants/compta";
// @ts-expect-error pure js
import { currencyFormatter } from "../../../../api/Functions";
import type { Property } from "../../../../models/property";
import styles from "./CourrierTFToemForm.module.css";

interface TaxBalance {
  date_transac?: string;
  type?: string;
  montant: number;
}

interface TfToemFormValues {
  montant: number | "";
  tva: boolean;
  taux_tva: number;
}

interface TvaOption {
  value: number;
  label: string;
}

const tvaRates: TvaOption[] = TVA_RATES;

function getLatestTaxBalance(
  balances: TaxBalance[] | undefined,
  type: string,
): TaxBalance | null {
  if (!balances || balances.length === 0) {
    return null;
  }
  const currentYear = new Date().getFullYear();

  const filtered = balances.filter((balance) => {
    if (!balance?.date_transac || !balance?.type) return false;
    const year = new Date(balance.date_transac).getFullYear();
    return year === currentYear && balance.type === type;
  });

  if (filtered.length === 0) {
    return null;
  }

  return filtered.reduce<TaxBalance | null>((latest, current) => {
    if (!latest) return current;
    return new Date(current.date_transac!) > new Date(latest.date_transac!)
      ? current
      : latest;
  }, null);
}

interface CourrierTFToemFormProps {
  property?: Property;
  courrierId: string;
}

export default function CourrierTFToemForm({
  property,
  courrierId,
}: CourrierTFToemFormProps) {
  const { values, setFieldValue } = useFormikContext<TfToemFormValues>();

  const { balances: propertyBalances } = useBalances(
    BalanceDomain.PROPERTY,
    property?.id,
  );

  const taxType = courrierId === "courrier_toem" ? "toem" : "impots_tf";

  const latestTaxBalance = useMemo(
    () => getLatestTaxBalance(propertyBalances, taxType),
    [propertyBalances, taxType],
  );

  useEffect(() => {
    if (!latestTaxBalance) {
      return;
    }
    // On ne remplit que si le champ est vide pour ne pas écraser une saisie manuelle
    if (!values.montant) {
      setFieldValue("montant", Math.abs(latestTaxBalance.montant));
    }
  }, [latestTaxBalance, setFieldValue, values.montant]);

  const montantHT = Math.abs(Number(values.montant)) || 0;
  const tauxTva = values.tva ? Number(values.taux_tva) || 0 : 0;
  const montantTTC = Math.round(montantHT * (1 + tauxTva / 100) * 100) / 100;
  const montantTVA = Math.round((montantTTC - montantHT) * 100) / 100;

  return (
    <div className={styles.root}>
      {latestTaxBalance && (
        <Callout
          variant="info"
          title={
            courrierId === "courrier_toem"
              ? "TOEM trouvée dans la comptabilité du bien pour l'année en cours :"
              : "Taxe foncière trouvée dans la comptabilité du bien pour l'année en cours :"
          }
        >
          {moment(latestTaxBalance.date_transac).format("DD/MM/YYYY")} –{" "}
          {currencyFormatter(Math.abs(latestTaxBalance.montant))}
        </Callout>
      )}
      <TextInput
        type={"number"}
        name={"montant"}
        label={values.tva ? "Montant HT" : "Montant"}
        placeholder={"48,20"}
        unit={"€"}
        step={"0.01"}
        mandatory
        marginForm=""
      />
      <div className={styles.tvaRow}>
        <Checkbox name="tva">Soumis à la TVA</Checkbox>
        {values.tva && (
          <ReactSelectStandalone
            className={styles.tauxSelect}
            options={tvaRates}
            value={tvaRates.find((o) => o.value === values.taux_tva) ?? null}
            onChange={(option: TvaOption | null) =>
              setFieldValue("taux_tva", option?.value ?? 20)
            }
            isClearable={false}
            placeholder="Taux"
          />
        )}
      </div>
      {values.tva && montantHT > 0 && (
        <Text variant="deci">
          TVA : {currencyFormatter(montantTVA)} | Montant appelé au locataire :{" "}
          {currencyFormatter(montantTTC)} TTC
        </Text>
      )}
    </div>
  );
}
